// The shared modal shell — every overlay (MediaLightbox, ContactSheet, Mosaic)
// renders through this so they all get identical behaviour:
//
//  - Portaled to document.body (createPortal), so the modal ESCAPES the app shell's
//    `.app { z-index: 1 }` stacking context and actually paints OVER the sticky
//    z-index:40 header — a descendant z-index can't climb out of a parent stacking
//    context, which is why an inline overlay rendered behind the header.
//  - The shared `useModal` behaviour: focus move-in + trap + restore, Escape, and a
//    backdrop click, gated by an optional `canClose()` predicate.
//  - The one `.sheet-overlay` / `__panel` / `__close` frame, previously copy-pasted
//    identically across the three overlays.

import { createPortal } from "react-dom";
import { useModal } from "../useModal";

export interface ModalProps {
  /** The dialog's accessible name. */
  label: string;
  /** Close the overlay (Escape / backdrop / ×, all gated by `canClose`). */
  onClose: () => void;
  /** Refuse a close while this returns false (e.g. a Mosaic mid-encode). */
  canClose?: () => boolean;
  /** Disable the × button — a visible busy guard alongside `canClose`. */
  closeDisabled?: boolean;
  /** `data-testid` on the overlay root. */
  testId?: string;
  /** Extra class on the overlay root (e.g. "media-lightbox"). */
  className?: string;
  children: React.ReactNode;
}

export function Modal({
  label,
  onClose,
  canClose,
  closeDisabled,
  testId,
  className,
  children,
}: ModalProps): React.JSX.Element {
  const { panelRef, requestClose, onBackdropClick } = useModal({ onClose, canClose });
  return createPortal(
    <div
      className={"sheet-overlay" + (className ? ` ${className}` : "")}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      data-testid={testId}
      onClick={onBackdropClick}
    >
      <div className="sheet-overlay__panel" ref={panelRef}>
        <button
          type="button"
          className="sheet-overlay__close"
          aria-label="Close"
          onClick={requestClose}
          disabled={closeDisabled}
        >
          ×
        </button>
        {children}
      </div>
    </div>,
    document.body,
  );
}
